/**
 * field-rules.js — 宣告式欄位驗證規則層
 *
 * 不重寫既有驗證引擎（admin-form-edit.js 的 validateField / initSubmit / clinic-hours.js 自有驗證
 * 全數保留）。本層只提供一個「宣告式規則插槽」：欄位以 data-rules="ruleA ruleB:arg" 宣告，
 * 引擎在既有 HTML5 旗標判斷之後、checkValidity() 兜底之前呼叫 runDeclaredRules() 跑這些規則。
 *
 * 設計約束（嚴格遵守）：
 *   - 每條規則簽名固定 (control, arg, form) => string|null。
 *   - 回傳值嚴格只能 null（通過）或「中文錯誤字串」（不通過）。不得回 boolean / undefined / 物件。
 *   - 規則名一律小寫；帶參數用冒號 + 單一 arg（如 checkbox-min:2）。
 *   - 規則為純函式：只讀 control / form 的 DOM，不寫 DOM、不發 side effect。
 *
 * 階段 0：尚無任何頁面帶 data-rules，故 runDeclaredRules 對現有 21 頁全惰性（讀到空 dataset 即回 null）。
 *
 * 載入：純規則層，不 auto-init。由 admin-form-edit.js（validateField）與 field-factory.js import
 *   使用（皆在 /src/admin.js 鏈）。檔內已有兩處 TODO(backend)：license-no / approval-no 的字號 pattern
 *   未定案 + 唯一性比對待後端 async 驗。
 */

/* 共用 helper（純函式，不碰 DOM 以外狀態） */
const val = (c) => (c && c.value != null ? String(c.value) : '');
const trimmed = (c) => val(c).trim();

/* "HH:MM" / 日期字串 → Date|null（date-after / date-max-today 用；交給瀏覽器 Date 解析 ISO 日期） */
function toDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* 今日 00:00（date-max-today 比較用，避免時分秒造成誤判） */
function todayStart() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/* 手機 / 市話 pattern（tw-mobile / tw-tel / phone 共用）。
 * 市話寫法分隔符多樣（02-2771-1234 / 03-123-4567 / 037-123456 / 純數字），故先 normalize 去掉
 * dash 與空白再比對純數字 pattern——比「允許 N 段可選 dash」的正則更穩健、不會被段落切法誤擋。 */
const normalizeDigits = (s) => s.replace(/[\s-]/g, '');
const RE_MOBILE = /^09\d{8}$/;        // 去 dash 後：09 開頭共 10 碼
const RE_TEL = /^0\d{1,2}\d{6,8}$/;   // 去 dash 後：0 + 區碼(1~2 碼) + 用戶碼(6~8 碼)
const RE_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const isMobile = (s) => RE_MOBILE.test(normalizeDigits(s));
const isTel = (s) => RE_TEL.test(normalizeDigits(s));

/**
 * 規則表。key = 規則名（小寫），value = (control, arg, form) => string|null。
 * 空值是否該擋由 no-blank 專責；多數型別規則「空值放行」（讓必填與格式正交，可組合宣告）。
 */
const RULE_REGISTRY = {
  /* 非空白：trim 後為空 → 擋 */
  'no-blank': (c) => (trimmed(c) === '' ? '此欄不可為空白' : null),

  /* 台灣手機：09 開頭共 10 碼（dash / 空白選用，如 0912-345-678）。空值放行（必填交 no-blank）。 */
  'tw-mobile': (c) => {
    const v = trimmed(c);
    if (v === '') return null;
    return isMobile(v) ? null : '請輸入正確的手機號碼（09 開頭共 10 碼）';
  },

  /* 台灣市話：含區碼（dash / 空白選用，如 02-2771-1234）。空值放行。 */
  'tw-tel': (c) => {
    const v = trimmed(c);
    if (v === '') return null;
    return isTel(v) ? null : '請輸入正確的市話（含區碼）';
  },

  /* 手機或市話擇一（兩者皆容許 dash / 空白分隔）。空值放行。 */
  phone: (c) => {
    const v = trimmed(c);
    if (v === '') return null;
    return isMobile(v) || isTel(v) ? null : '請輸入正確的手機或市話號碼';
  },

  /* Email。空值放行（必填交 no-blank；HTML5 type=email 仍會走 validity 路徑兜底）。 */
  email: (c) => {
    const v = trimmed(c);
    if (v === '') return null;
    return RE_EMAIL.test(v) ? null : '請輸入有效的 Email，例如 name@example.com';
  },

  /* 網址：用 new URL() 驗 protocol + host。空值放行（必填另由 no-blank 管）。 */
  url: (c) => {
    const v = trimmed(c);
    if (v === '') return null;
    try {
      const u = new URL(v);
      // 需有 protocol（http/https）與 host，避免 "foo:" / 相對路徑被誤判為合法
      if (!u.protocol || !u.host) return '請輸入完整網址，需含 https://（例：https://example.com）';
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return '請輸入完整網址，需含 https://（例：https://example.com）';
      return null;
    } catch (_) {
      return '請輸入完整網址，需含 https://（例：https://example.com）';
    }
  },

  /* 執照字號：pattern 未定案前先「trim 非空 + 長度下限」。唯一性待後端（TODO）。
   * 空值放行（必填交 no-blank），有值才驗長度。 */
  'license-no': (c) => {
    const v = trimmed(c);
    if (v === '') return null;
    // TODO(backend): 字號 pattern 未定案 + 唯一性比對（衛福部登錄資料）待後端 async 驗。
    // TODO(backend): 字號 pattern 定案 + 唯一性 async 驗證（比對衛福部登錄），清 data-pending-unique — 見 docs/BACKEND_HANDOFF.md#field-rules-unique-async
    if (v.length < 4) return '請輸入有效的執照字號（至少 4 碼）';
    return null;
  },

  /* 核准字號：同 license-no（pattern + 唯一性 async stub）。前端先過、頁面端可掛 data-pending-unique。
   * 空值放行。 */
  'approval-no': (c) => {
    const v = trimmed(c);
    if (v === '') return null;
    // TODO(backend): 核准字號 pattern 未定案 + 唯一性 async 驗（data-pending-unique 標記待後端回填）。
    if (v.length < 4) return '請輸入有效的核准字號（至少 4 碼）';
    return null;
  },

  /**
   * 群組勾選下限：control 為 .check-grid / [data-field-group] 群容器，數其內 checked 數量 >= N（預設 1）。
   * @param {HTMLElement} c 群容器
   * @param {string} arg 下限（字串）；非數字 / 缺省 → 1
   */
  'checkbox-min': (c, arg) => {
    const n = Number.parseInt(arg, 10);
    const min = Number.isFinite(n) && n > 0 ? n : 1;
    const checked = c ? c.querySelectorAll('input[type="checkbox"]:checked').length : 0;
    return checked >= min ? null : `請至少勾選 ${min} 項`;
  },

  /**
   * 條件式必填：control.dataset.ruleWhen 指向一個 toggle（id 或 name）；toggle.checked 且本欄 trim 空 → 擋。
   * toggle 未勾 → 放行（此欄非必填）。
   */
  'conditional-required': (c, arg, form) => {
    const toggleSel = c.dataset.ruleWhen;
    if (!toggleSel) return null;
    const toggle = findPartner(form, toggleSel);
    if (!toggle) return null;            // 找不到 toggle → 不擋（宣告錯誤不應誤殺使用者）
    if (!toggle.checked) return null;    // 未勾 → 非必填
    return trimmed(c) === '' ? '勾選後此欄為必填' : null;
  },

  /**
   * 到期日須晚於核准日：control.dataset.ruleTarget 指向夥伴日期欄（核准日）；本欄（到期日） <= 夥伴 → 擋。
   * 任一為空或無法解析 → 放行（必填 / 格式各自由其他規則管）。
   */
  'date-after': (c, arg, form) => {
    const targetSel = c.dataset.ruleTarget;
    if (!targetSel) return null;
    const partner = findPartner(form, targetSel);
    if (!partner) return null;
    const self = toDate(val(c));
    const other = toDate(val(partner));
    if (!self || !other) return null;
    return self.getTime() > other.getTime() ? null : '到期日須晚於核准日';
  },

  /* 日期不可晚於今日（核准日 / 發證日等）。空值或無法解析 → 放行。 */
  'date-max-today': (c) => {
    const self = toDate(val(c));
    if (!self) return null;
    self.setHours(0, 0, 0, 0);
    return self.getTime() > todayStart().getTime() ? '日期不可晚於今日' : null;
  },

  /* 富文本非空：剝掉標籤後 trim 為空 → 擋。control 多為承接富文本輸出的 hidden input（value=HTML）。
   * 可選條件：control.dataset.ruleWhen 指向一個 toggle（id 或 name），未命中（toggle 未勾 / 不存在）
   * → 放行（允許空）；命中才強制非空。對齊 conditional-required 的 when 語義，用於
   * 「發布時才強制非空、存草稿允許空」（platform-privacy）。無 data-rule-when → 一律強制（向後相容）。 */
  'richtext-nonempty': (c, arg, form) => {
    const toggleSel = c.dataset ? c.dataset.ruleWhen : null;
    if (toggleSel) {
      const toggle = findPartner(form, toggleSel);
      if (!toggle || !toggle.checked) return null; // 條件未成立（如草稿態）→ 不強制
    }
    const stripped = val(c).replace(/<[^>]*>/g, '').trim();
    return stripped === '' ? '內容不可為空' : null;
  },

  /* 檔案：型別 / 大小已由 admin-form-edit.js 的 initUploads 即時驗，submit 兜底交 validateRequiredUploads。
   * 此處先回 null（保留 hook，未來如需 submit 層型別兜底再於此補）。 */
  file: () => null,
};

/**
 * 找夥伴控制項（conditional-required 的 toggle / date-after 的 target 日期欄）。
 * selector 約定：優先當成 id（#id 或裸 id），找不到再退而求 name。限制在 form 範圍內找。
 * @param {HTMLElement} form
 * @param {string} sel id 或 name
 * @returns {HTMLElement|null}
 */
function findPartner(form, sel) {
  if (!form || !sel) return null;
  const id = sel.startsWith('#') ? sel.slice(1) : sel;
  return (
    form.querySelector(`#${CSS.escape(id)}`) ||
    form.querySelector(`[name="${CSS.escape(id)}"]`) ||
    null
  );
}

/**
 * 解析並執行 control 上宣告的規則，回第一個錯誤字串或 null。
 * data-rules 以空白分隔，每條可帶單一冒號參數（如 "no-blank checkbox-min:2"）。
 * 未知規則名靜默略過（不誤殺）。
 * @param {HTMLElement} control 帶 data-rules 的欄位 / 群容器
 * @param {HTMLFormElement} [form] control.form 或群容器的 closest('form')
 * @returns {string|null}
 */
function runDeclaredRules(control, form) {
  if (!control || !control.dataset || !control.dataset.rules) return null;
  const scope = form || control.form || (control.closest ? control.closest('form') : null);
  const tokens = control.dataset.rules.trim().split(/\s+/).filter(Boolean);
  for (const token of tokens) {
    const idx = token.indexOf(':');
    const name = (idx === -1 ? token : token.slice(0, idx)).toLowerCase();
    const arg = idx === -1 ? undefined : token.slice(idx + 1);
    const fn = RULE_REGISTRY[name];
    if (typeof fn !== 'function') continue; // 未知規則略過
    const msg = fn(control, arg, scope);
    if (msg != null) return msg; // 第一個錯誤即回（短路）
  }
  return null;
}

export { RULE_REGISTRY, runDeclaredRules };
